import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
	FormGroup,
	FormText,
	Label,
	Modal,
	ModalBody,
	ModalHeader,
	ModalFooter,
} from 'reactstrap';
import { v4 as uuid } from 'uuid';

import { Button, DangerAlert, IconButton, getLanguageOptions, SCOPES } from '../../../../_shared';
import { messages } from '../../../../_shared/i18n/messages';
import {
	LibraryUploader,
	LibraryVideo,
	MasterAccessField,
	ENUMS_VIDEO,
	MUX_AUTO_GENERATED_LANGUAGES,
	videoTypes,
	updateVideo,
	deleteVideo,
} from '../../../../_video';

import Input from '../Input';
import SelectInput from '../SelectInput';

import './VideoInput.scss';

const propTypes = {

	/**
	 * The input name.
	 */
	name: PropTypes.string.isRequired,

	/**
	 * The input value. A video if editing or null if new.
	 */
	value: videoTypes.video,

	/**
	 * The input error, if any.
	 */
	error: PropTypes.string,


	/**
	 * The provider for the video. Currently only supports Mux.
	 */
	videoProvider: PropTypes.oneOf([
		ENUMS_VIDEO.PROVIDER.MUX,
	]).isRequired,

	/**
	 * The scope.
	 */
	scope: PropTypes.oneOf([
		SCOPES.SESSION,
		SCOPES.ORGANIZATION,
	]).isRequired,

	/**
	 * The scope ID.
	 */
	scopeId: PropTypes.string.isRequired,

	/**
	 * True, if the user is an admin.
	 */
	isAdmin: PropTypes.bool,

	/**
	 * True, if editing the input value.
	 */
	isEditing: PropTypes.bool,

	/**
	 * True, if the input value is invalid.
	 */
	isInvalid: PropTypes.bool,

	/**
	 * True, if the input value is disabled.
	 */
	disabled: PropTypes.bool,

	/**
	 * The aspect ratio.
	 */
	aspectRatio: PropTypes.string,

	/**
	 * Callback function to handle changes to the input value.
	 */
	onChange: PropTypes.func,

};

/**
 * Displays a button which opens a modal to upload a video file
 * to our Library along with video settings. Requires a video provider. Currently
 * only supports a provider of Mux.
 *
 * Contains advanced options to set a custom thumbnail image timestamp.
 *
 * @param {object} props  The component props.
 * @returns {object}      The component.
 */
const VideoInput = (props) => {

	const {
		name,
		value,
		videoProvider,
		error,
		scope,
		scopeId,
		isAdmin = false,
		isEditing,
		isInvalid,
		disabled,
		aspectRatio,
		onChange,
	} = props;

	const intl = useIntl();

	// Show the uploader if there is no video or if the video is awaiting upload
	const showUploader = !value || Boolean(
		value?.status === ENUMS_VIDEO.STATUS.WAITING &&
		value?.upload_url
	);

	// Show the remove button when there is an ID or error
	const showRemoveButton = Boolean(value?.id) || Boolean(error);

	// Show the video preview when the form has video ID and status is ready
	// or if it has an error
	const showVideoPreview =
		(Boolean(value?.id) && value?.status === ENUMS_VIDEO.STATUS.READY) ||
		Boolean(error);

	const [isOpen, setIsOpen] = useState(false);
	const [hasError, setHasError] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [formData, setFormData] = useState(buildFormData(value));

	useEffect(() => { setFormData(buildFormData(value)); }, [value]);

	const handleChange = (fieldName, fieldValue) => {
		setFormData((prev) => ({ ...prev, [fieldName]: fieldValue }));
	};

	const handleSubmit = () => {
		setHasError(false);

		// Update the video data with the latest form data
		const updatedVideo = {
			...value,
			...formData
		};

		if (updatedVideo.thumbnail_timestamp === '') {
			updatedVideo.thumbnail_timestamp = null;
		}

		// Send request to update the video
		updateVideo(updatedVideo)
			.then(() => {
				setIsOpen(false);
			})
			.catch((err) => {
				setHasError(true);
				console.error(err);
			});

	};


	/**
	 * Handles response from modal confirming if the video should be
	 * deleted from the library in addition to being removed from the form.
	 *
	 * @param {boolean} deleteFromLibrary
	 * Flag indicating if the video should be deleted from the library.
	 */
	const handleDelete = (deleteFromLibrary) => {

		onChange(null);
		setShowDeleteModal(false);

		if (!value?.id) { return; }

		if (deleteFromLibrary) {
			deleteVideo(value.id, scope, scopeId)
				.catch((err) => {
					console.error(err);
				});
		}

	};

	// Options for the generated subtitles form field
	const generatedLanguageCodeOptions = getLanguageOptions().filter((lang) => {
		return MUX_AUTO_GENERATED_LANGUAGES.includes(lang.value);
	});

	// Add a none option
	generatedLanguageCodeOptions.unshift({
		label: intl.formatMessage(messages.TC_COMPONENTS_GENERAL_FIELD_OPTION_NONE),
		value: '',
	});

	// Create a unique modal ID
	const modalId = useMemo(() => {
		return `form-field-video-advanced-options-${uuid()}`;
	}, []);

	return (
		<div
			id={name}
			className={`tc-material-input-video ${isInvalid ? 'tc-material-input-invalid' : ''}`}>
			<div className="d-flex">
				<Button
					className="my-2 d-flex me-2 align-items-center"
					color="primary"
					outline
					onClick={() => { setIsOpen(true); }}>
					<>
						<i
							className={`far ${value?.id ? 'fa-pencil-alt' : 'fa-circle-plus'} fa-sm me-2`}
							aria-label="none" />
						{value?.id
							? <FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_EDIT_BUTTON_TEXT} />
							: <FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_ADD_BUTTON_TEXT} />
						}
					</>
				</Button>
				{showRemoveButton && isEditing && !disabled &&
					<Button
						className="me-auto my-2 align-items-center"
						color="danger"
						outline
						onClick={() => setShowDeleteModal(true)}>
						<>
							<i
								className="fa fa-trash fa-sm me-2"
								aria-label="none" />
							<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_REMOVE_BUTTON_TEXT} />
						</>
					</Button>
				}
			</div>
			<Modal
				id={modalId}
				size="lg"
				isOpen={isOpen}>
				<ModalHeader
					close={
						<IconButton
							icon="far fa-xmark text-muted fa-lg"
							title={intl.formatMessage(messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL)}
							size="30px"
							className="modal-close-button"
							onClick={() => setIsOpen(false)} />
					}>
					{value?.id
						? <FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_MODAL_HEADER_EDIT_VIDEO} />
						: <FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_MODAL_HEADER_CREATE_VIDEO} />
					}
				</ModalHeader>
				<ModalBody>
					{hasError &&
						<DangerAlert>
							<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_SUBMIT_ERROR} />
						</DangerAlert>
					}
					<FormGroup
						className="tc-form-field"
						tag="div">
						<Label
							for="tc-live-stream-form-title-field"
							className="tc-form-field-label-text">
							<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_TITLE_FORM_FIELD_LABEL} />
						</Label>
						<Input
							id="tc-live-stream-form-title-field"
							type="text"
							name="title"
							label={intl.formatMessage(messages.TC_COMPONENTS_VIDEO_INPUT_TITLE_FORM_FIELD_LABEL)}
							value={formData?.title}
							isEditing
							onChange={(newValue) => handleChange('title', newValue)} />
					</FormGroup>
					{!value?.id &&
						<FormGroup
							className="tc-form-field"
							tag="div">
							<Label
								for="generated_subtitles_language"
								className="tc-form-field-label-text">
								<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_LIBRARY_UPLOADER_GENERATED_SUBTITLES_LABEL} />
							</Label>
							<FormText
								className="tc-form-field-help-text"
								aria-live="assertive"
								tag="div">
								<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_LIBRARY_UPLOADER_GENERATED_SUBTITLES_HELP} />
							</FormText>
							<SelectInput
								name="generated_subtitles_language"
								value={formData?.generated_subtitles_language}
								options={generatedLanguageCodeOptions}
								isEditing
								onChange={(newValue, optionId) =>
									handleChange('generated_subtitles_language', newValue, optionId)
								} />
						</FormGroup>
					}
					{value?.id &&
						<>
							<FormGroup
								className="tc-form-field"
								tag="div">
								<Label
									for="thumbnail_timestamp"
									className="tc-form-field-label-text">
									<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_THUMBNAIL_FORM_FIELD_LABEL} />
								</Label>
								<FormText
									className="tc-form-field-help-text"
									aria-live="assertive"
									tag="div">
									<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_THUMBNAIL_FORM_FIELD_HELP_TEXT} />
								</FormText>
								<Input
									type="number"
									name="thumbnail_timestamp"
									label={intl.formatMessage(messages.TC_COMPONENTS_VIDEO_INPUT_THUMBNAIL_FORM_FIELD_LABEL)}
									value={formData?.thumbnail_timestamp}
									isEditing
									onChange={(newValue) => handleChange('thumbnail_timestamp', newValue)} />
							</FormGroup>
							{isAdmin &&
								<MasterAccessField
									video={value} />
							}
						</>
					}
					{showUploader && isEditing && !disabled &&
						<LibraryUploader
							video={value}
							provider={videoProvider}
							options={formData}
							scope={scope}
							scopeId={scopeId}
							onCreateVideo={(createdVideo) => onChange(createdVideo.id)} />
					}
				</ModalBody>
				<ModalFooter>
					<Button
						className="me-2"
						color="neutral"
						outline
						onClick={() => setIsOpen(false)}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_CANCEL_LABEL} />
					</Button>
					<Button
						type="submit"
						color="primary"
						onClick={handleSubmit}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_SAVE_LABEL} />
					</Button>
				</ModalFooter>
			</Modal>
			{showVideoPreview &&
				<LibraryVideo
					title="preview"
					contentType="video-input"
					aspectRatio={aspectRatio}
					video={value}
					error={error}
					isAdmin />
			}
			<Modal
				id="video-input-delete-video-modal"
				size="lg"
				isOpen={showDeleteModal}
				toggle={() => setShowDeleteModal((prev) => !prev)}>
				<ModalHeader>
					<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_DELETE_MODAL_TITLE} />
				</ModalHeader>
				<ModalBody>
					<FormattedMessage {...messages.TC_COMPONENTS_VIDEO_INPUT_DELETE_MODAL_BODY} />
				</ModalBody>
				<ModalFooter>
					<Button
						className="me-1"
						color="neutral"
						outline
						onClick={() => handleDelete(false)}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_NO} />
					</Button>
					<Button
						color="primary"
						onClick={() => handleDelete(true)}>
						<FormattedMessage {...messages.TC_COMPONENTS_GENERAL_TEXT_YES} />
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);

};

VideoInput.propTypes = propTypes;

export default VideoInput;

/**
 * Builds the initial data for the form
 * based on the existing data for the event, if any.
 *
 * @param {Object} [data] The existing video data, if any.
 *
 * @returns {Object} result The initial form data.
 */
function buildFormData(data) {

	const initialValues = data || {};

	// Set the ID as the title if one is not provided
	if (!initialValues?.title && initialValues?.id) {
		initialValues.title = initialValues.id;
	}

	// If no ID, create the ID and title
	if (!initialValues?.title && !initialValues?.id) {

		const id = uuid();

		initialValues.id = id;
		initialValues.title = id;

	}

	return initialValues;

}
